import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { canonicalPlayerStatistics, playerStatisticValue } from '../../utils/playerStatistics';
import { playerMetric } from '../../utils/playerRanking';
import { useTheme } from '@mui/material/styles';
import { isGoalkeeper } from '../../utils/playerDiscovery';
import PlayerRoleBadge from '../playerStatistics/PlayerRoleBadge';

const LABELS = {
  number: 'number', name: 'player', goals: 'goals', appearances: 'pj',
  minutes: 'minutes', starts: 'starters', callups: 'callups',
  minGoal: 'minutesPerGoal', goalsGame: 'goalsPerGame', captain: 'captainTimes',
};
const value = (player, key) => {
  if (key === 'name') return player.nombre || '';
  if (key === 'number') return player.dorsalMasUsado ?? 0;
  if (key === 'captain') return playerStatisticValue(player, 'partidos', 'vecesCapitanLocal') +
    playerStatisticValue(player, 'partidos', 'vecesCapitanVisitante');
  const metric = {
    goals: 'golesTotales', appearances: 'partidosJugados', minutes: 'minutosJugados',
    starts: 'titularidades', callups: 'convocatorias', minGoal: 'minutosPorGol', goalsGame: 'golesPorPartido',
  }[key];
  return playerMetric(canonicalPlayerStatistics(player), metric);
};

function PlayerTable({ title, players, columns, sortable = false, profileContext }) {
  const { t, i18n } = useTranslation('reports');
  const theme = useTheme();
  const [sort, setSort] = useState(null);
  const formatter = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 });
  const ordered = [...players];
  if (sort) ordered.sort((a, b) => {
    const first = value(a, sort.key), second = value(b, sort.key);
    return (sort.direction === 'ascending' ? 1 : -1) *
      (sort.key === 'name' ? first.localeCompare(second, i18n.language) : Number(first) - Number(second));
  });
  const maximumMinutes = Math.max(0, ...players.map(player => value(player, 'minutes')));
  return <div className="rr-table-scroll" role="region" aria-label={title} tabIndex={0}>
    <table className="rr-people-table" aria-label={title}>
      <thead><tr>{columns.map(key => <th key={key} scope="col"
        aria-sort={sortable ? (sort?.key === key ? sort.direction : 'none') : undefined}>
        {sortable ? <button type="button" onClick={() => setSort({
          key, direction: sort?.key === key && sort.direction === 'ascending' ? 'descending' : 'ascending',
        })}>{t(`table.${LABELS[key]}`)}<span aria-hidden="true">
          {sort?.key === key ? (sort.direction === 'ascending' ? '↑' : '↓') : '↕'}
        </span></button> : t(`table.${LABELS[key]}`)}
      </th>)}</tr></thead>
      <tbody>{ordered.map((player, index) => <tr key={player.id ?? `${player.idJugador}:${index}`}
        className={isGoalkeeper(player) ? 'player-goalkeeper' : undefined} data-theme={theme.palette.mode}>
        {columns.map(key => {
          const metric = value(player, key);
          if (key === 'name') {
            const captainCount = value(player, 'captain');
            const captainLabel = t('modern.people.captainRecorded', { count: captainCount });
            const season = player.idTemporada ?? profileContext?.season;
            const documentId = String(player.id ?? '').trim();
            const playerId = String(player.idJugador ?? '').trim();
            const profileParams = profileContext && season != null && (documentId || playerId)
              ? new URLSearchParams({ [documentId ? 'id' : 'idJugador']: documentId || playerId,
                idTemporada: String(season) }) : null;
            return <th scope="row" key={key}><span className="rr-player-name">
              {profileParams ? <Link className="rr-player-profile" to={`/jugador?${profileParams}`}
                state={{ jugador: player, directoryFrom: profileContext.directoryFrom }}>{metric}</Link> : metric}
              {captainCount > 0 && <span className="rr-captain-badge" role="img"
                aria-label={captainLabel} title={captainLabel}>C</span>}
              <PlayerRoleBadge player={player} />
            </span>
              {isGoalkeeper(player) && <small className="rr-player-role">{t('modern.goalkeeper')}</small>}</th>;
          }
          return <td key={key}>{key === 'number' ? <span className="rr-shirt">{metric}</span> :
            <span className={key === 'minutes' ? 'rr-minute-value' : undefined}>
              {formatter.format(metric)}
              {key === 'minutes' && <span className="rr-minute-track" aria-hidden="true">
                <i style={{ width: `${maximumMinutes > 0 ? Math.max(0, Math.min(100, metric / maximumMinutes * 100)) : 0}%` }} />
              </span>}
            </span>}</td>;
        })}
      </tr>)}</tbody>
    </table>
  </div>;
}

export function ReportCoaching({ staff, matchCoach, team, players = [] }) {
  const { t } = useTranslation('reports');
  const current = staff?.actual;
  const headCoach = current?.entrenadorPrincipal || matchCoach;
  const coaches = [
    { name: headCoach, label: t('coaching.headCoach', { team }), primary: true },
    ...(current?.entrenadorSecundario ? [{ name: current.entrenadorSecundario, label: t('coaching.assistant') }] : []),
  ];
  return <section className="rr-panel rr-coaching" aria-label={t('sections.technicalStaff')}>
    <header className="rr-panel-heading"><div>
      <span className="rr-eyebrow">{t('modern.people.eyebrow')}</span>
      <h2>{t('sections.technicalStaff')}</h2>
    </div><span className="rr-squad-count">{t('modern.people.players', { count: players.length })}</span></header>
    {!headCoach && !current ? <p className="rr-empty">{t('coaching.noDataDesc')}</p> :
      <div className="rr-coach-grid">{coaches.map(({ name, label, primary }) => {
        const recorded = name && name !== 'No presenta';
        return <article className={`rr-coach${primary ? ' rr-coach--head' : ''}`} key={label}>
          <span className="rr-coach-avatar" aria-hidden="true">{recorded
            ? name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('') : 'CT'}</span>
          <div><span className="rr-coach-role">{label}</span>
            <h3>{recorded ? name : t('coaching.noCoach')}</h3>
          </div>
        </article>;
      })}</div>}
    {staff?.haCambiadoAlgunaVez && staff.historialCambios?.length > 0 &&
      <p className="rr-coach-change">{t('coaching.coachChanges', { count: staff.numeroCambios ?? 0 })}</p>}
  </section>;
}

export function ReportPlayerLeaders({ scorers = [], minutes = [] }) {
  const { t } = useTranslation('reports');
  return <div className="rr-people-leaders">
    <section className="rr-panel" aria-label={t('sections.scorers')}>
      <header><span className="rr-eyebrow">{t('modern.people.finishing')}</span><h2>{t('sections.scorers')}</h2></header>
      {scorers.length ? <PlayerTable title={t('sections.scorers')} players={scorers} sortable
        columns={['number', 'name', 'goals', 'appearances', minutes.length ? 'minGoal' : 'goalsGame']} /> :
        <p className="rr-empty">{t('empty.noScorersDesc')}</p>}
    </section>
    {minutes.length > 0 && <section className="rr-panel" aria-label={t('sections.mostMinutes')}>
      <header><span className="rr-eyebrow">{t('modern.people.continuity')}</span><h2>{t('sections.mostMinutes')}</h2></header>
      <PlayerTable title={t('sections.mostMinutes')} players={minutes.slice(0, 5)}
        columns={['number', 'name', 'minutes', 'starts', 'appearances']} />
    </section>}
  </div>;
}

export function ReportSquad({ players = [], keyPlayers = [], profileContext }) {
  const { t } = useTranslation('reports');
  const captains = players.filter(player => value(player, 'captain') > 0)
    .sort((a, b) => value(b, 'captain') - value(a, 'captain'));
  return <div className="rr-squad">
    {captains.length > 0 && <section className="rr-panel" aria-label={t('sections.captains')}>
      <header><span className="rr-eyebrow">{t('modern.people.leadership')}</span><h2>{t('sections.captains')}</h2></header>
      <PlayerTable title={t('sections.captains')} players={captains} columns={['number', 'name', 'captain']}
        profileContext={profileContext} />
    </section>}
    {keyPlayers.length > 0 && <section className="rr-panel rr-key-players" aria-label={t('sections.keyPlayers')}>
      <header><span className="rr-eyebrow">{t('modern.people.watch')}</span><h2>{t('sections.keyPlayers')}</h2>
        <p>{t('keyPlayers.warning')}</p></header>
      <PlayerTable title={t('sections.keyPlayers')} players={keyPlayers}
        profileContext={profileContext}
        columns={['number', 'name', 'goals', 'minutes', 'appearances', 'starts', 'minGoal']} />
    </section>}
    <section className="rr-panel" aria-label={t('sections.squad')}>
      <header className="rr-panel-heading"><div><span className="rr-eyebrow">{t('modern.people.everyPlayer')}</span>
        <h2>{t('sections.squad')}</h2></div><span className="rr-squad-count">
        {t('modern.people.players', { count: players.length })}</span></header>
      {players.length ? <PlayerTable title={t('sections.squad')} players={players} sortable
        profileContext={profileContext}
        columns={['number', 'name', 'minutes', 'starts', 'callups', 'appearances']} /> :
        <p className="rr-empty">{t('empty.noPlayersDesc')}</p>}
    </section>
  </div>;
}
